// Approval entity and action hashing — see FD §5 and §21 (Approvals).
//
// "De goedkeuring wordt gekoppeld aan een hash van de exacte voorgenomen
// actie. Bij wijziging van de actie vervalt de approval." (FD §21.2)
// The hash function below is the canonical implementation both the API
// (when creating/checking an approval) and the web UI (when confirming
// nothing changed before submitting an approve click) must use, so it lives
// here rather than being re-implemented per package. It only uses the Web
// Crypto API, which is available in both Node (>=19) and every modern
// browser, keeping this file safe to import from @goblic/web.

import type { ApprovalStatus, ApprovalType } from '../constants/index.js'
import type { WorkflowGateContext } from './task.js'

// ---------------------------------------------------------------------------
// Proposed action — the thing an Approval grants or denies (FD §21.1, §21.2)
// ---------------------------------------------------------------------------

interface ApprovalActionBase {
  runId: string
}

export interface BudgetOrTimeExceededAction extends ApprovalActionBase {
  kind: 'budget_or_time_exceeded'
  limit: 'cost' | 'tokens' | 'duration' | 'tool_calls'
  currentValue: number
  limitValue: number
}

export interface PrivilegedToolAction extends ApprovalActionBase {
  kind: 'privileged_or_destructive_tool'
  mcpServiceId: string
  toolName: string
  params: Record<string, unknown>
}

export interface NewNetworkDomainAction extends ApprovalActionBase {
  kind: 'new_network_domain'
  domain: string
}

export interface BranchPushAction extends ApprovalActionBase {
  kind: 'branch_push'
  repositoryUrl: string
  branch: string
  commit: string
}

export interface PullRequestAction extends ApprovalActionBase {
  kind: 'pull_request_create_or_ready'
  repositoryUrl: string
  branch: string
  title: string
  markReady: boolean
}

export interface FollowUpAgentAction extends ApprovalActionBase {
  kind: 'follow_up_agent'
  nextAgentId: string
  nextTaskTitle: string
}

/**
 * @deprecated Memory moved to the knowledge base (migration 0019); no run
 * raises this any more. Kept so historical approvals still type-check and display.
 */
export interface PersistentMemorySaveAction extends ApprovalActionBase {
  kind: 'persistent_memory_save'
  memoryProposalId: string
}

export interface DeploymentMergeOrReleaseAction extends ApprovalActionBase {
  kind: 'deployment_merge_or_release'
  target: 'merge' | 'deployment' | 'release'
  repositoryUrl: string
  ref: string
}

/** One sub-question of a multi-question `agent_question` approval — mirrors the catalog's `AskHumanQuestionItem`. */
export interface AgentQuestionItem {
  id?: string
  question: string
  askKind?: 'text' | 'yes_no' | 'single_choice' | 'multiple_choice'
  options?: string[]
  allowOther?: boolean
}

/**
 * The run's `ask_human` tool call (run-tools catalog, plan 04 §2.4). Answered
 * with the `answer` decision, not approve/reject — free text for `askKind:
 * 'text'`/`'yes_no'` (absent `askKind` means 'text', from before this field
 * existed), one of `options` for `'single_choice'`, several for
 * `'multiple_choice'`, plus a free-text "other" answer when `allowOther` is
 * set (catalog's `AskHumanInputSchema`). When `questions` is set, `question`
 * is the form's intro/title and each item in `questions` is answered
 * separately (the `'answer'` decision's `answers` array, one per item, in
 * the same order) — see `encodeAnswerNote()`/`formatAgentAnswer()`.
 */
export interface AgentQuestionAction extends ApprovalActionBase {
  kind: 'agent_question'
  question: string
  context?: string
  askKind?: 'text' | 'yes_no' | 'single_choice' | 'multiple_choice'
  options?: string[]
  allowOther?: boolean
  /** Several sub-questions asked in one `ask_human` call, shown to the human as one form. */
  questions?: AgentQuestionItem[]
}

/** The run's `request_approval` tool call (run-tools catalog, plan 04 §2.4). Approve/reject like any other approval, except a reject sends the run back to `running` instead of failing it. */
export interface AgentRequestAction extends ApprovalActionBase {
  kind: 'agent_request'
  action: string
  reason: string
  risk: 'low' | 'medium' | 'high' | 'critical'
}

/**
 * A task reached a human state of its workflow. Unlike every other kind this
 * one is not raised by a run, so it carries no `runId`: the approval row has
 * `runId: null` and `taskId` set. Approving/rejecting moves the task to
 * `onApprove`/`onReject`.
 */
export interface WorkflowGateAction {
  kind: 'workflow_gate'
  taskId: string
  workflowKey: string
  state: string
  onApprove: string | null
  onReject: string | null
  /** The human step's instructions: what the reviewer should check. */
  instructions?: string | null
  /** The task's workflow variables when the gate was raised (`{ name: { value, reason } }`), so the reviewer sees the decisions made so far. */
  variables?: Record<string, { value: string; reason: string | null }>
}

/** Discriminated union of every action type that can require an Approval. */
export type ApprovalAction =
  | BudgetOrTimeExceededAction
  | PrivilegedToolAction
  | NewNetworkDomainAction
  | BranchPushAction
  | PullRequestAction
  | FollowUpAgentAction
  | PersistentMemorySaveAction
  | DeploymentMergeOrReleaseAction
  | AgentQuestionAction
  | AgentRequestAction
  | WorkflowGateAction

/** Every approval kind that a run raises (all but `workflow_gate`). */
export type RunApprovalAction = Exclude<ApprovalAction, WorkflowGateAction>

// ---------------------------------------------------------------------------
// Canonical hashing
// ---------------------------------------------------------------------------

/**
 * Deterministically stringifies a JSON-serializable value: object keys are
 * sorted recursively so that two structurally-equal actions always produce
 * the same string, regardless of key insertion order.
 */
export function canonicalStringify(value: unknown): string {
  const sortKeys = (input: unknown): unknown => {
    if (Array.isArray(input)) {
      return input.map(sortKeys)
    }
    if (input !== null && typeof input === 'object') {
      const entries = Object.entries(input as Record<string, unknown>)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, val]) => [key, sortKeys(val)] as const)
      return Object.fromEntries(entries)
    }
    return input
  }
  return JSON.stringify(sortKeys(value))
}

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Canonical hash of the exact proposed action, per FD §21.2. Uses SHA-256
 * over the canonical (sorted-key) JSON representation via Web Crypto, so it
 * runs identically in the API, the worker and the browser.
 */
export async function hashApprovalAction(
  action: ApprovalAction
): Promise<string> {
  const canonical = canonicalStringify(action)
  const subtle = globalThis.crypto?.subtle
  if (!subtle) {
    // TODO: Node <19 / non-browser fallback (e.g. a `node:crypto` based
    // implementation loaded only in server contexts) if this platform ever
    // needs to support an environment without Web Crypto.
    throw new Error('Web Crypto (crypto.subtle) is not available in this environment')
  }
  const digest = await subtle.digest(
    'SHA-256',
    new TextEncoder().encode(canonical)
  )
  return toHex(digest)
}

/** Convenience check used before honoring an approve click in the UI/API. */
export async function isApprovalActionUnchanged(
  action: ApprovalAction,
  approvedActionHash: string
): Promise<boolean> {
  return (await hashApprovalAction(action)) === approvedActionHash
}

// ---------------------------------------------------------------------------
// Approval entity (FD §21.2)
// ---------------------------------------------------------------------------

export interface Approval {
  id: string
  /** Null for `workflow_gate` approvals (no run involved). */
  runId: string | null
  /** Set for `workflow_gate` approvals; may be null for run-raised kinds. */
  taskId?: string | null
  type: ApprovalType
  action: ApprovalAction
  actionHash: string
  reason: string
  /** Null for `workflow_gate` approvals (no agent involved). */
  agentId: string | null
  projectId: string
  riskIndicator: 'low' | 'medium' | 'high' | 'critical'
  expectedCostCents: number | null
  status: ApprovalStatus
  expiresAt: string
  resolvedBy: string | null
  resolvedAt: string | null
  additionalInstruction: string | null
  createdAt: string
  /** Set only for a `workflow_gate` approval: what changed, the runs that led here, and the handoff message, so the reviewer can judge the work (see `WorkflowGateContext`'s own comment). */
  gateContext?: WorkflowGateContext | null
}
